import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  useGetFeaturedCoachesQuery,
  useCreateEngagementMutation,
  useRequestBookingMutation,
  type CoachProfile,
} from '../../store/api/humanCoachingApi';
import { LoadErrorNotice } from '../../components/LoadErrorNotice';

// Display type for expert cards (mapped from API CoachProfile)
// No rating, review or session count: the coach API has none, and the
// fabricated zeros labelled every coach "New coach". The price is the coach's
// one-time (per-session) rate — it was shown as an hourly rate.
interface DisplayExpert {
  id: string;
  coachUserId?: string;
  name: string;
  specialty: string;
  bio: string;
  sessionRate: number;
  tags: string[];
  availability: string;
  sessionLengthMinutes?: number;
}

export function mapCoachToExpert(coach: CoachProfile): DisplayExpert {
  const firstName = coach.user?.firstName ?? '';
  const lastName = coach.user?.lastName ?? '';
  const name = `${firstName} ${lastName}`.trim() || coach.headline;
  return {
    id: coach.userId,
    coachUserId: coach.userId,
    name,
    specialty: coach.specialties[0] ?? 'General',
    bio: coach.bio ?? coach.headline,
    sessionRate: coach.rateOneTime ?? 0,
    tags: coach.specialties,
    availability: coach.isAcceptingClients ? 'Available' : 'Busy',
    sessionLengthMinutes: coach.sessionLengthMinutes,
  };
}

export function ExpertsPage() {
  const [selectedSpecialty, setSelectedSpecialty] = useState('All');
  const [selectedExpert, setSelectedExpert] = useState<string | null>(null);

  // Fetch ALL coaches (no specialty filter) to derive available specialties
  const { data: allCoachData } = useGetFeaturedCoachesQuery({ limit: 100 });

  // Derive specialty tabs dynamically from coaches who are actually on the platform
  const specialties = useMemo<string[]>(() => {
    if (!allCoachData?.coaches?.length) return ['All'];
    const set = new Set<string>();
    allCoachData.coaches.forEach((c) => c.specialties.forEach((s) => set.add(s)));
    return ['All', ...Array.from(set).sort()];
  }, [allCoachData]);

  // Fetch coaches filtered by selected specialty (backend-filtered via JSONB containment)
  const queryParams = useMemo(
    () => ({
      ...(selectedSpecialty !== 'All' && { specialty: selectedSpecialty }),
      limit: 20,
    }),
    [selectedSpecialty]
  );
  const { data: coachData, isLoading, isError, error, refetch } = useGetFeaturedCoachesQuery(queryParams);

  // Map API coaches to display format — no client-side filtering, backend handles it
  const experts = useMemo<DisplayExpert[]>(() => {
    if (coachData?.coaches?.length) {
      return coachData.coaches.map(mapCoachToExpert);
    }
    return [];
  }, [coachData]);

  const expert = selectedExpert ? experts.find((e: DisplayExpert) => e.id === selectedExpert) : null;

  return (
    <div className="space-y-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500">
        <Link to="/marketplace" className="hover:text-primary-600">Marketplace</Link>
        <span>/</span>
        <span className="text-gray-900">Expert Coaches</span>
      </nav>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Expert Coaches</h1>
        <p className="mt-1 text-gray-500">
          Connect with professionals for personalized coaching and feedback
        </p>
      </div>

      {/* Specialty filters — derived dynamically from backend data */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {specialties.map((specialty) => (
          <button
            key={specialty}
            onClick={() => setSelectedSpecialty(specialty)}
            className={clsx(
              'px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
              selectedSpecialty === specialty
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            {specialty}
          </button>
        ))}
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-12 text-gray-500">Loading coaches...</div>
      )}

      {/* A failed load is not "No coaches found". */}
      {!isLoading && isError && (
        <LoadErrorNotice
          error={error}
          onRetry={() => void refetch()}
          failedMessage="Coaches could not be loaded."
          testIdPrefix="experts"
        />
      )}

      {/* Expert grid */}
      {!isLoading && !isError && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {experts.map((expertItem) => (
            <ExpertCard
              key={expertItem.id}
              expert={expertItem}
              onViewProfile={() => setSelectedExpert(expertItem.id)}
            />
          ))}
          {experts.length === 0 && (
            <div className="col-span-full text-center py-8 text-gray-500">
              <p>No coaches found{selectedSpecialty !== 'All' ? ' for this specialty' : ''}.</p>
              <p className="text-sm text-gray-400 mt-1">Check back later as coaches join the platform.</p>
            </div>
          )}
        </div>
      )}

      {/* Expert profile modal */}
      {expert && (
        <ExpertProfileModal
          expert={expert}
          onClose={() => setSelectedExpert(null)}
        />
      )}
    </div>
  );
}

interface ExpertCardProps {
  expert: DisplayExpert;
  onViewProfile: () => void;
}

function ExpertCard({ expert, onViewProfile }: ExpertCardProps) {
  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="card-body">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
            <span className="text-xl font-bold text-white">{expert.name[0]}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">{expert.name}</h3>
              <span
                className={clsx(
                  'w-2 h-2 rounded-full',
                  expert.availability === 'Available' ? 'bg-green-500' : 'bg-yellow-500'
                )}
                title={expert.availability}
              />
            </div>
            <p className="text-sm text-primary-600">{expert.specialty}</p>
          </div>
        </div>

        {/* Bio */}
        <p className="text-sm text-gray-500 mt-3 line-clamp-2">{expert.bio}</p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mt-3">
          {expert.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
              {tag}
            </span>
          ))}
        </div>

        {/* Price and action */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          {expert.sessionRate > 0 ? (
            <span className="text-lg font-bold text-gray-900">${expert.sessionRate} / session</span>
          ) : (
            <span className="text-lg font-bold text-gray-400">Rate TBD</span>
          )}
          <button
            onClick={onViewProfile}
            className="btn-primary"
          >
            View Profile
          </button>
        </div>
      </div>
    </div>
  );
}

interface ExpertProfileModalProps {
  expert: DisplayExpert;
  onClose: () => void;
}

function ExpertProfileModal({ expert, onClose }: ExpertProfileModalProps) {
  const [createEngagement] = useCreateEngagementMutation();
  const [requestBooking] = useRequestBookingMutation();
  const [bookingState, setBookingState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [bookingError, setBookingError] = useState<string | null>(null);

  const handleBookSession = async () => {
    if (!expert.coachUserId) {
      return;
    }

    setBookingState('loading');
    setBookingError(null);

    try {
      // Step 1: Create a one-time engagement with this coach
      const engagement = await createEngagement({
        coachUserId: expert.coachUserId,
        type: 'one_time',
        planLabel: `Session with ${expert.name}`,
        sessionLengthMinutes: expert.sessionLengthMinutes ?? 60,
      }).unwrap();

      // Step 2: Request a booking on the engagement
      await requestBooking({
        engagementId: engagement.id,
        durationMinutes: expert.sessionLengthMinutes ?? 60,
        notes: 'Booking requested via Expert Marketplace',
      }).unwrap();

      setBookingState('success');
    } catch (err: unknown) {
      setBookingState('error');
      const message = (err as { data?: { message?: string } })?.data?.message;
      setBookingError(message ?? 'Failed to create booking. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <CloseIcon className="w-6 h-6" />
          </button>

          {/* Header */}
          <div className="p-6 border-b">
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                <span className="text-3xl font-bold text-white">{expert.name[0]}</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{expert.name}</h2>
                <p className="text-primary-600">{expert.specialty}</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Bio */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">About</h3>
              <p className="text-gray-600">{expert.bio}</p>
            </div>

            {/* Expertise */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Expertise</h3>
              <div className="flex flex-wrap gap-2">
                {expert.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-primary-50 text-primary-700 text-sm rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">
                  {expert.sessionRate > 0 ? `$${expert.sessionRate}` : 'TBD'}
                </div>
                <div className="text-xs text-gray-500">Per Session</div>
              </div>
              {expert.sessionLengthMinutes && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{expert.sessionLengthMinutes}</div>
                  <div className="text-xs text-gray-500">Min / Session</div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t bg-gray-50 rounded-b-xl">
            {bookingState === 'success' ? (
              <div className="text-center py-2">
                <p className="text-green-600 font-medium">Session requested</p>
                <p className="text-sm text-gray-500 mt-1">
                  The coach confirms the session and arranges any payment with you directly.
                  Nothing has been charged.
                </p>
              </div>
            ) : (
              <>
                <button
                  className={clsx(
                    'w-full py-3 rounded-lg font-medium transition-colors',
                    expert.availability === 'Available'
                      ? 'btn-primary'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  )}
                  disabled={bookingState === 'loading' || expert.availability !== 'Available'}
                  onClick={handleBookSession}
                >
                  {bookingState === 'loading'
                    ? 'Requesting...'
                    : expert.availability !== 'Available'
                      ? 'Currently Unavailable'
                      : expert.sessionRate > 0
                        ? `Request a session · $${expert.sessionRate}, arranged with the coach`
                        : 'Request a session'}
                </button>
                {bookingError && (
                  <p className="text-sm text-red-600 text-center mt-2">{bookingError}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Icon components

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
